import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import {Link, useNavigate} from "react-router-dom";
import Paper from "@mui/material/Paper";
import * as React from "react";
import {useEffect} from "react";
import useStore from "../store/useStore";
import HouseRoundedIcon from '@mui/icons-material/HouseRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import { useLocation } from 'react-router-dom';
import api from "../utils/api";
import * as util from "../utils/util";
import { shouldForceLogout } from "../utils/authCheck";

interface Window {
  ReactNativeWebView: {
    postMessage: (message: string) => void;
  };
}

const Bottom = () => {
  const setBottomNavValue = useStore((state) => state.setBottomNavValue);
  const bottomNavValue = useStore((state) => state.bottomNavValue);
  const location = useLocation();
  const [step, setStep] = React.useState(0);
  const navigate = useNavigate();

  /**
   * 세션이 끊겼을 때의 처리.
   *
   * 예전에는 `navigate('/')` 로 홈에 떨궜다. 두 가지가 문제였다.
   *  1) 로그아웃됐다고 알려만 주고 로그인 화면은 보여주지 않아, 사용자가 다시
   *     들어가려면 전체메뉴에서 로그인을 직접 찾아야 했다.
   *  2) push 라서 안드로이드 뒤로가기를 누르면 방금 튕겨나온 회원 전용 화면으로
   *     되돌아갔다. 그 화면은 로그아웃 상태로 마운트돼 빈 껍데기만 그린다.
   * 그래서 replace 로 바꾸고, 보던 화면을 state.from 으로 넘겨 로그인 후 되돌아오게 한다.
   * (from 키는 RequireLogin·loginRedirect 와 같은 규약이며 Login 화면이 검증한다)
   */
  const forceLogout = () => {
    util.clearLoginStorage();
    alert("로그인 정보가 변경되어 로그아웃 되었습니다.");
    navigate('/login', {
      replace: true,
      state: { from: location.pathname + location.search },
    });
  };

  useEffect(() => {
    if (util.strNull(window.localStorage.userCusID) === '') return;

    // 걸음수 저장을 위한 리스너 android, ios 구분하는 코드
    const receiver: any = navigator.userAgent.includes("Android")
      ? document
      : window;
    receiver.addEventListener("message", listener);
    postMessage('GetSteps', '');

    // Bottom 은 대부분의 화면에 렌더되므로 정리하지 않으면 화면을 옮길 때마다
    // 리스너가 누적된다(계측 결과 14회 이동에 22개). 그러면 메시지 1건에
    // 언마운트된 컴포넌트의 setStep 이 누적 개수만큼 호출된다.
    return () => {
      receiver.removeEventListener("message", listener);
    };
  }, []);
  
  useEffect(() => {
    const pathname = location.pathname;
    if(pathname === '/'){
      setBottomNavValue('home');
    }else if(pathname.startsWith('/reservation')){
      setBottomNavValue('reservation');
    }else if(pathname.startsWith('/care')){
      setBottomNavValue('care');
    }else if(pathname.startsWith('/check')){
      setBottomNavValue('check');
    }else if(pathname.startsWith('/history')){
      setBottomNavValue('history');
    }else if(pathname.startsWith('/digitalTwin')){
      // 디지털 트윈은 '전체' 메뉴에서 진입하므로 하단 탭도 '전체'로 표시한다
      setBottomNavValue('MoreMenu');
    }else if(pathname.startsWith('/MoreMenu') || pathname.startsWith('/News') || pathname.startsWith('/Notice')
      || pathname.startsWith('/Policy') || pathname.startsWith('/Version') || pathname.startsWith('/MyPage')){
      setBottomNavValue('MoreMenu');
    }
    
    // 로그인 안되어있거나, 유효하지 않은 토큰을 가지고 있을 때 => 로그아웃
    // ex) 앱 환경에서 로그인 되어 있는 상황에서, 고객 웹에서 로그인하면 앱 환경에서는 강제 로그아웃 처리
    const token = window.localStorage.getItem("token");
    // 이름 주의: 저장 키 'userCusID' 는 고객번호(20000009), 'userID' 는 로그인 아이디(12344575)다.
    // isAuth 응답의 id/userId 는 고객번호, user_id 는 로그인 아이디에 대응한다.
    const cusId: any = localStorage.getItem('userCusID');
    const loginId: any = localStorage.getItem('userID');

    if (token == null || cusId == null) {
      util.clearLoginStorage();
    } else {
      api.post('permission/isAuth', { token: token }).then(res => {
        // message 가 아니라 응답에 담긴 신원으로 판정한다.
        // 서버가 유효한 토큰에도 message="Failed" 를 주기 시작해(2026-08-13),
        // 문자열만 보던 기존 코드는 정상 로그인 상태에서도 화면을 옮길 때마다
        // 강제 로그아웃시켰다. 자세한 근거는 utils/authCheck.ts 주석 참고.
        if (shouldForceLogout(res.data, util.strNull(loginId), util.strNull(cusId))) {
          forceLogout();
        } else {
          // 심리상담 메뉴 노출 관리
          api.post('customer/api/v1/checkCounselTarget', { customerId: localStorage.getItem('userCusID') }).then(res => {
            localStorage.setItem("openConsultationReservation", res.data.data.isTarget);
          }).catch(function (error) {
            console.log(error);
          });
        }
      }).catch(function (error) {
        // isAuth 호출이 실패한 것과 인증이 거부된 것은 다르다.
        // Bottom 은 대부분의 화면에 렌더되므로, 여기서 무조건 경고하고 홈으로 보내면
        // 타임아웃(axios timeout 5s)·네트워크 단절·서버 5xx 같은 일시적 상황에서도
        // 사용자가 쓰던 화면에서 튕기고 "잘못된 접근입니다" 를 본다.
        // (디지털 트윈 화면은 13MB 모델을 동시에 받으므로 특히 타임아웃이 나기 쉽다)
        // 인증 거부(401/403)만 로그아웃으로 처리하고, 나머지는 로그만 남긴다.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          forceLogout();
          return;
        }
        console.log(error);
      });
    }
  }, []);
  
  const postMessage = (type: string, data: any) => {
    var win: any = window;
    win.ReactNativeWebView?.postMessage(JSON.stringify({ type, data }));
  };

  const listener = (event: any) => {
    if (typeof event.data !== 'string') return;  // web => object, app => string

    // 앱이 보낸 JSON 이 아닌 문자열 메시지(HMR, 브라우저 확장 등)도 이 리스너로 들어온다.
    // 방어 없이 파싱하면 그때마다 예외가 발생한다.
    let appData: any;
    try {
      appData = JSON.parse(event.data);
    } catch {
      return;
    }

    if (appData && appData.type === "steps") {
      setStep(appData.data);
    }
  };
  
  // 걸음수 저장
  function handleClickMoveHistory(){
    const saveParams = { date: '', step: 0 };
    saveParams.date = util.getDateByCustom('yyyy-MM-dd', 0);
    saveParams.step = step;
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      api.post(`app/api/v1/AP_002_009`, saveParams).then(res => {
      }).catch(function (error) {
        console.log(error)
      });
    }
  }



  return (
    <Paper sx={BottomNavi} elevation={6}>
      <BottomNavigation
        showLabels
        value={bottomNavValue}
        onChange={(event, newValue) => {
          if(newValue !== 'home'){
            handleClickMoveHistory();
          }
          setBottomNavValue(newValue);
        }}
      >
        <BottomNavigationAction
          component={Link}
          to="/"
          label="홈"
          value="home"
          icon={<HouseRoundedIcon />}
          sx={BottomMenu}
          className="BottomMenu"
        />
        <BottomNavigationAction
          component={Link}
          to="/reservation"
          label="예약"
          value="reservation"
          sx={BottomMenu}
          icon={<EventAvailableRoundedIcon />}
        />

        <BottomNavigationAction
          component={Link}
          to="/check"
          label="체크"
          value="check"
          sx={BottomMenu}
          icon={<VolunteerActivismRoundedIcon />}
        />

        <BottomNavigationAction
          component={Link}
          to="/care"
          label="케어"
          value="care"
          sx={BottomMenu}
          icon={<FactCheckRoundedIcon />}
        />
        <BottomNavigationAction
          component={Link}
          to="/history"
          label="기록"
          value="history"
          sx={BottomMenu}
          icon={<ManageSearchRoundedIcon />}
        />
        <BottomNavigationAction
          component={Link}
          to="/MoreMenu"
          label="전체"
          value="MoreMenu"
          sx={BottomMenu}
          icon={<DensityMediumIcon />}
        />
        

        {/* {(['right'] as const).map((anchor) => (
           
           <React.Fragment key={anchor}>
             <BottomNavigationAction onClick={toggleDrawer(anchor, true)}
               component={Link}
               to=""
               label="전체"
               value="all"
               sx={BottomMenu}
               icon={<DensityMediumIcon />}
             />
            
             <SwipeableDrawer
               anchor={anchor}
               open={state[anchor]}
               onClose={toggleDrawer(anchor, false)}
               onOpen={toggleDrawer(anchor, true)}
             >
               {list(anchor)}
             </SwipeableDrawer>
           </React.Fragment>
         
         ))} */}


          
        
      </BottomNavigation>
      
    </Paper>
  );
}

const BottomNavi ={
  position: 'fixed', 
  bottom: 0, left: 0, right: 0, borderRadius:'20px 20px 0 0', 
  zIndex:2,
  '& .MuiBottomNavigation-root' : { borderRadius:'20px 20px 0 0', borderTop:'1px solid #ccc'},
}

const BottomMenu = {
  width:'auto',
  maxWidth:'auto',
  minWidth:'auto',
  p:0,
  color: '#777',
  fontSize:12,
  '& span' : { opacity:1, pt:'2px'},
  '&.Mui-selected .MuiSvgIcon-root':{color:'#EF8322'},
  '&.Mui-selected span':{color:'#EF8322',fontSize:12,},
} 

export default Bottom;